import React, { useEffect, useState } from 'react';
import { hrAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';

export default function SimpleMasterPage({ kind, title, singular, seed }) {
  const { user } = useAuth();
  const canManage = user?.role === 'admin' || user?.app_roles?.hr_training === 'manager';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null); // {id?, name, description}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setRows((await hrAPI.listMaster(kind)).data || []); }
    catch { toast.error(`Failed to load ${title}`); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const save = async () => {
    if (!dlg.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (dlg.id) await hrAPI.updateMaster(kind, dlg.id, { name: dlg.name, description: dlg.description || '' });
      else await hrAPI.createMaster(kind, { name: dlg.name, description: dlg.description || '' });
      toast.success(`${singular} saved`);
      setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try { await hrAPI.deleteMaster(kind, row.id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const runSeed = async () => {
    try { const r = await hrAPI.seedDefaults(); toast.success(r.data.message); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-5" data-testid={`hr-${kind}-page`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0F172A]">{title}</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage {title.toLowerCase()} for the HR module</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            {seed && <Button size="sm" variant="outline" onClick={runSeed} data-testid={`hr-${kind}-seed`}>Seed defaults</Button>}
            <Button size="sm" className="bg-pink-600 hover:bg-pink-700 text-white gap-1" onClick={() => setDlg({ name: '', description: '' })} data-testid={`hr-${kind}-add`}>
              <Plus className="w-4 h-4" /> Add {singular}
            </Button>
          </div>
        )}
      </div>

      <Card className="border border-slate-200 shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80">
                <TableHead className="text-xs font-semibold text-slate-500">Name</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500">Description</TableHead>
                {canManage && <TableHead className="text-xs font-semibold text-slate-500 w-24 text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={3} className="text-center text-slate-400 py-8 text-sm">Loading...</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={3} className="text-center text-slate-400 py-8 text-sm">No {title.toLowerCase()} yet</TableCell></TableRow>
              ) : rows.map(r => (
                <TableRow key={r.id} data-testid={`hr-${kind}-row-${r.id}`}>
                  <TableCell className="text-sm font-medium">{r.name}</TableCell>
                  <TableCell className="text-sm text-slate-500">{r.description || '—'}</TableCell>
                  {canManage && (
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setDlg({ id: r.id, name: r.name, description: r.description || '' })} data-testid={`hr-${kind}-edit-${r.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => remove(r)} data-testid={`hr-${kind}-del-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid={`hr-${kind}-dialog`}>
          <DialogHeader>
            <DialogTitle>{dlg?.id ? `Edit ${singular}` : `Add ${singular}`}</DialogTitle>
            <DialogDescription className="text-xs">Enter the details below</DialogDescription>
          </DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div><Label className="text-xs">Name *</Label><Input value={dlg.name} onChange={e => setDlg({ ...dlg, name: e.target.value })} data-testid={`hr-${kind}-name-input`} autoFocus /></div>
              <div><Label className="text-xs">Description</Label><Textarea rows={2} value={dlg.description} onChange={e => setDlg({ ...dlg, description: e.target.value })} data-testid={`hr-${kind}-desc-input`} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid={`hr-${kind}-save`}>{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
